export type NovelFeatureSpoil =
  | 'timeline'
  | 'character-analysis'
  | 'character-relationships'
  | 'character-chat';

const NOVEL_FEATURE_SPOIL_KEYS: Record<NovelFeatureSpoil, string> = {
  timeline: 'timeline_spoil',
  'character-analysis': 'character-analysis_spoil',
  'character-relationships': 'character-relationship_spoil',
  'character-chat': 'character-chat_spoil',
};

const getNovelFeatureSpoilKey = (feature: NovelFeatureSpoil) =>
  NOVEL_FEATURE_SPOIL_KEYS[feature];

const getLegacyNovelFeatureSpoilKey = (
  bookId: number,
  feature: NovelFeatureSpoil
) => `spoil_feature:${feature}:book_id:${bookId}`;

const readSpoiledBookIds = (feature: NovelFeatureSpoil): number[] => {
  const key = getNovelFeatureSpoilKey(feature);

  try {
    const value = window.localStorage.getItem(key);
    if (!value) return [];

    const parsed = JSON.parse(value);
    if (!Array.isArray(parsed)) return [];

    return [
      ...new Set(
        parsed
          .map(bookId => (typeof bookId === 'string' ? Number(bookId) : bookId))
          .filter(
            (bookId): bookId is number => Number.isInteger(bookId) && bookId > 0
          )
      ),
    ];
  } catch {
    return [];
  }
};

const migrateLegacySpoilValue = (
  bookId: number,
  feature: NovelFeatureSpoil
): boolean => {
  try {
    return (
      window.localStorage.getItem(
        getLegacyNovelFeatureSpoilKey(bookId, feature)
      ) === 'true'
    );
  } catch {
    return false;
  }
};

export const getNovelFeatureSpoil = (
  bookId: number | undefined,
  feature: NovelFeatureSpoil
): boolean => {
  if (!bookId || typeof window === 'undefined') return false;

  try {
    const spoiledBookIds = readSpoiledBookIds(feature);
    if (spoiledBookIds.includes(bookId)) return true;

    // Preserve existing user preferences while moving them to the grouped format.
    if (migrateLegacySpoilValue(bookId, feature)) {
      setNovelFeatureSpoil(bookId, feature, true);
      window.localStorage.removeItem(
        getLegacyNovelFeatureSpoilKey(bookId, feature)
      );
      return true;
    }

    return false;
  } catch {
    return false;
  }
};

export const setNovelFeatureSpoil = (
  bookId: number | undefined,
  feature: NovelFeatureSpoil,
  value: boolean
) => {
  if (!bookId || typeof window === 'undefined') return;

  try {
    const key = getNovelFeatureSpoilKey(feature);
    const spoiledBookIds = readSpoiledBookIds(feature);
    const nextBookIds = value
      ? [...new Set([...spoiledBookIds, bookId])]
      : spoiledBookIds.filter(id => id !== bookId);

    window.localStorage.setItem(key, JSON.stringify(nextBookIds));
  } catch {
    // A blocked or unavailable localStorage must not affect the feature.
  }
};
